import React, { useEffect, useMemo, useState } from 'react'
import { X, Loader2, ScanLine, Send, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, RotateCcw, LogIn, BookOpen } from 'lucide-react'
import LoginModal from '../../components/auth/LoginModal'
import ScanCapture from '../lesson-builder/components/ScanCapture'
import KaTeXRenderer from '../../components/common/KaTeXRenderer'
import StudentAIReviewPanel from '../../components/ai/StudentAIReviewPanel'
import { API_CONFIG } from '../../config/api.config'
import { studentAi, token, StudentAIError } from '../../services/studentAi'
import {
  fetchSheetExam, normalizeCell, buildSubmitAnswers, correctOnSheet, solutionOnSheet, cellKey, scale10, dsSlots,
  type SheetExam, type SheetGroup, type SheetRow,
} from './sheetModel'
import { loadSource, canvasToBlob } from '../lesson-builder/lib/docScan'
import { extractSheetCells, findMarkerQuad, expandQuad, lastSheetFailure, lastWarped } from './sheetVision'
import { ocrCells, buildMosaics, type CellReading } from './sheetOcr'
import { loadModels, classifyCell } from './sheetClassifier'
import { SubmitIdentityFields, loadIdentity, saveIdentity, identityOk, identityPayload, type Identity } from './SubmitIdentity'

// Nộp bài bằng PHIẾU ĐÁP ÁN GIẤY: chụp → AI đọc chữ viết tay → HỌC SINH XÁC NHẬN (kể cả MÃ ĐỀ) →
// chấm ở server (cùng /pmaker-advanced/submit như làm online) → điểm thang 10 + đáp án + hướng dẫn giải.
// Ô phiếu được nhớ theo VỊ TRÍ trong phần (cellKey) → đổi mã đề không mất đáp án đã đọc/sửa.

type Stage = 'loading' | 'login' | 'capture' | 'reading' | 'confirm' | 'submitting' | 'result'
// Việc đang dở khi bị 401 (chưa đăng nhập / phiên hết hạn): đăng nhập xong làm tiếp, không bắt chụp lại.
type Pending = { kind: 'read'; file: File } | { kind: 'submit' } | null
const TITLES: Record<SheetGroup, string> = { TN: 'I. Trắc nghiệm', DS: 'II. Đúng sai', TL: 'III. Trả lời ngắn' }
const GROUPS: SheetGroup[] = ['TN', 'DS', 'TL']
const ROMAN: Record<SheetGroup, string> = { TN: 'I', DS: 'II', TL: 'III' }
const PER_ROW: Record<SheetGroup, number> = { TN: 10, DS: 4, TL: 6 }   // số ô mỗi hàng trên phiếu in
// Ngưỡng tự tin để bộ nhận dạng trên máy tự nhận (không cần AI, không cần trùng OCR)
// Phần III (số, dấu phẩy hay dính vào chữ số; sai là mất điểm): LUÔN để AI kiểm tra — tối đa 6 ô/phiếu
const HW_STRONG: Record<SheetGroup, number> = { TN: 0.97, DS: 0.95, TL: 1.01 }

// Giá trị MÁY đọc (OCR/AI) → ô bảng. Chỉ nhận ký tự hợp lệ: Phần I A–D, Phần II Đ/S — ký tự khác bị bỏ
// thành trống + tô vàng để học sinh tự điền. Phần III giữ nguyên (tô đỏ nếu sai mẫu) vì số có thể đúng một phần.
const machineCell = (group: SheetGroup, raw: string): { value: string; ok: boolean } => {
  if (group === 'TN') { const n = normalizeCell(group, raw); return n.ok ? n : { value: '', ok: false } }
  if (group !== 'DS') return normalizeCell(group, raw)
  const d = dsSlots(raw)
  return { value: d.value, ok: !d.dropped && !d.value.includes('_') }
}
// Học sinh gõ: chỉ giữ ký tự hợp lệ của phần đó
const typedCell = (group: SheetGroup, raw: string): string =>
  group === 'TN' ? raw.toUpperCase().replace(/[^ABCD]/g, '').slice(-1) : raw.replace(/[^0-9,.\-]/g, '').slice(0, 8)

const clusterContextOf = (exam: SheetExam, row: SheetRow): string => {
  const cid = row.q?.examMetadata?.cluster_id
  if (!cid) return ''
  for (const g of GROUPS) for (const r of exam.groups[g]) {
    if (r.q?.examMetadata?.cluster_id === cid && r.q.examMetadata.cluster_context) return r.q.examMetadata.cluster_context
  }
  return ''
}

// initialToken = mã ký k trong QR phiếu GV phát → được AI kiểm tra dù chưa vào danh sách lớp của GV
const PaperSheetSubmit: React.FC<{ slug: string; initialCode?: string | null; initialToken?: string | null; onClose: () => void }> = ({ slug, initialCode, initialToken, onClose }) => {
  const [stage, setStage] = useState<Stage>('loading')
  const [code, setCode] = useState<string | null>(initialCode || null)
  const [exam, setExam] = useState<SheetExam | null>(null)
  const [photo, setPhoto] = useState<{ file: File; url: string } | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [unsure, setUnsure] = useState<Set<string>>(new Set())
  const [result, setResult] = useState<any>(null)
  const [openSet, setOpenSet] = useState<Set<string>>(new Set())
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')          // lỗi hiện TO ở đầu cửa sổ (không lẫn với ghi chú)
  const [loginOpen, setLoginOpen] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const [readNote, setReadNote] = useState('')
  const [identity, setIdentity] = useState<Identity>(() => loadIdentity())
  const [checking, setChecking] = useState(false)      // hộp "kiểm tra lại trước khi nộp"
  const [checked, setChecked] = useState(false)

  // Tải bố cục theo mã đề; đổi mã đề → tải lại bố cục, GIỮ nguyên các ô đã đọc.
  useEffect(() => {
    let alive = true
    fetchSheetExam(slug, code).then((e) => {
      if (!alive) return
      setExam(e)
      if (e.settings?.subject) setIdentity((v) => ({ ...v, subject: e.settings.subject }))
      setStage((s) => (s === 'loading' ? (token() ? 'capture' : 'login') : s))
    }).catch((e) => { if (alive) { setErr(e.message); setStage((s) => (s === 'loading' ? 'confirm' : s)) } })
    return () => { alive = false }
  }, [slug, code])

  // Đang mở hộp đăng nhập: chờ khoá đăng nhập MỚI xuất hiện rồi làm tiếp việc đang dở.
  useEffect(() => {
    if (!loginOpen) return
    const before = token()
    const t = setInterval(() => {
      const now = token()
      if (!now || now === before) return
      clearInterval(t)
      setLoginOpen(false); setErr('')
      const p = pending
      setPending(null)
      if (p?.kind === 'read') read(p.file)
      else if (p?.kind === 'submit') setStage('confirm')
      else setStage('capture')
    }, 800)
    return () => clearInterval(t)
  }, [loginOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const needLogin = (next: Pending, text: string) => {
    setPending(next); setErr(text); setStage('login')
  }
  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.url) }, [photo])

  // Tầng 1: 4 ô đen → cắt từng ô → OCR trên máy (miễn phí). Tầng 2: CHỈ các ô chưa chắc → AI (1 ảnh nhỏ).
  // Không thấy 4 ô đen / bảng → trả false để AI đọc cả phiếu như trước.
  const readCells = async (file: File, ex: SheetExam): Promise<boolean> => {
    setReadNote('Đang tìm 4 ô đen và cắt phiếu…')
    const src = await loadSource(file)
    const sections = GROUPS.filter((g) => ex.groups[g].length).map((g) => ({ group: g, count: ex.groups[g].length, per: PER_ROW[g] }))
    const cells = extractSheetCells(src, sections)
    if (!cells) return false
    if (lastWarped) {                                // hiện phiếu đã nắn thẳng để đối chiếu
      const wb = await canvasToBlob(lastWarped, 0.85, 1400)
      setPhoto({ file, url: URL.createObjectURL(wb) })
    }
    const readings = await ocrCells(cells, (d, t) => setReadNote(`Máy đang đọc chữ trên phiếu… ${d}/${t}`))
    // Tầng 1 (miễn phí, trên máy): bộ nhận dạng chữ viết tay (A–D, Đ/S, 0–9) + OCR đối chiếu.
    //   rất tự tin, hoặc khá tự tin VÀ trùng OCR → nhận luôn.
    // Tầng 2: CHỈ các ô còn lại → AI (ảnh ô to, chế độ high). AI trùng với 1 trong 2 bộ máy → tin; lệch → tô vàng.
    let models: Awaited<ReturnType<typeof loadModels>> | null = null
    try { models = await loadModels() } catch { models = null }
    const canvasOf = new Map(cells.map((c) => [cellKey(c), c.canvas]))
    const v: Record<string, string> = {}
    const u = new Set<string>()
    const hwOf = new Map<string, { value: string; conf: number } | null>()
    const doubt: CellReading[] = []
    let local = 0
    readings.forEach((r) => {
      const k = cellKey(r)
      if (r.blank) { v[k] = ''; return }
      const hw = models ? classifyCell(models, r.group, canvasOf.get(k)!) : null
      hwOf.set(k, hw)
      const ocr = machineCell(r.group, r.value)
      const hwm = hw ? machineCell(r.group, hw.value) : null
      const strong = HW_STRONG[r.group]
      if (hw && hwm && hwm.ok && hwm.value && (hw.conf >= strong || (r.group !== 'TL' && hw.conf >= 0.8 && r.sure && ocr.value === hwm.value))) {
        v[k] = hwm.value; local++
      } else {
        v[k] = (hwm && hwm.ok && hwm.value) ? hwm.value : (r.sure && ocr.ok ? ocr.value : '')
        doubt.push(r)
      }
    })
    const labelOf = (r: CellReading) => `${ROMAN[r.group]}-${ex.groups[r.group][r.index - 1]?.number ?? r.index}`
    let aiError = '', aiOk = 0, differ = 0
    if (doubt.length) {
      setReadNote(`AI kiểm tra ${doubt.length} ô máy đọc chưa chắc…`)
      try {
        const mosaics = await buildMosaics(doubt.map((r) => ({ label: labelOf(r), canvas: canvasOf.get(cellKey(r))! })), { tile: 170, cols: 3 })
        const values = (await studentAi.scanCells(ex.id, mosaics, doubt.map((r) => ({ label: labelOf(r), group: r.group })), ex.code, initialToken)).values || {}
        doubt.forEach((r) => {
          const k = cellKey(r)
          const ai = values[labelOf(r)] ? machineCell(r.group, values[labelOf(r)]) : null
          const hw = hwOf.get(k), hwv = hw ? machineCell(r.group, hw.value).value : ''
          const ocrv = machineCell(r.group, r.value).value
          if (ai && ai.ok && ai.value) {
            v[k] = ai.value
            if (ai.value === hwv || ai.value === ocrv) aiOk++
            else { u.add(k); differ++ }
          } else u.add(k)
        })
      } catch (e) {
        if ((e as StudentAIError).status === 401) throw e
        doubt.forEach((r) => u.add(cellKey(r)))
        aiError = (e as Error).message || 'lỗi không rõ'
      }
    }
    setValues(v); setUnsure(u); setStage('confirm')
    setMsg(`Đã tự cắt phiếu theo 4 ô đen · máy tự đọc chắc ${local} ô${doubt.length ? ` · AI kiểm tra ${doubt.length} ô` : ''}`
      + `${differ ? ` (${differ} ô AI và máy đọc khác nhau)` : ''}${u.size ? ` · ${u.size} ô cần soát (tô vàng)` : ''} — hãy đối chiếu với phiếu trước khi nộp.`)
    if (aiError) setErr(`AI chưa kiểm tra được ${doubt.length} ô (${aiError}). Hãy soát kỹ các ô tô vàng.`)
    return true
  }

  const read = async (file: File) => {
    if (!exam) return
    setPhoto({ file, url: URL.createObjectURL(file) })
    setStage('reading'); setMsg(''); setErr('')
    try {
      let handled = false, why = ''
      try { handled = await readCells(file, exam); why = lastSheetFailure }
      catch (e) { if ((e as StudentAIError).status === 401) throw e; why = (e as Error).message || 'lỗi OCR' }
      if (handled) return
      console.warn('[phieu] không tự cắt/OCR được:', why)
      const autoNote = `Chưa tự cắt được theo 4 ô đen (${why}) nên AI đọc cả phiếu. `
      setReadNote('AI đang đọc chữ viết trên cả phiếu…')
      const r = await studentAi.scanSheet(exam.id, file, exam.code, initialToken)
      const v: Record<string, string> = {}
      const u = new Set<string>()
      r.items.forEach((it) => {
        const k = cellKey({ group: it.group, index: it.index })
        const mc = machineCell(it.group, it.value)
        v[k] = mc.value
        if (!it.ok || !mc.ok) u.add(k)
      })
      setValues(v); setUnsure(u); setStage('confirm')
      if (!r.read) {
        setMsg(autoNote + 'Máy chưa đọc được ô nào trên phiếu (lượt này không bị tính). Hãy chụp lại: phiếu nằm trọn và thẳng trong khung, thấy rõ 4 ô đen, đủ sáng — hoặc tự nhập đáp án vào bảng dưới.')
        return
      }
      setMsg(autoNote + (u.size ? `Có ${u.size} ô máy đọc chưa chắc (tô vàng) — hãy đối chiếu với phiếu và sửa cho đúng.` : 'Máy đã đọc xong — hãy đối chiếu lại với phiếu trước khi nộp.'))
    } catch (e) {
      if ((e as StudentAIError).status === 401) {
        needLogin({ kind: 'read', file }, 'Phiên đăng nhập đã hết hạn hoặc bạn chưa đăng nhập. Hãy đăng nhập — ảnh phiếu vừa chụp vẫn được giữ, không cần chụp lại.')
        return
      }
      setErr(`Chưa đọc được phiếu: ${(e as Error).message || 'lỗi không rõ'}. Bấm "Chụp lại" hoặc tự nhập đáp án vào bảng dưới.`)
      setValues({}); setUnsure(new Set()); setStage('confirm')
    }
  }

  const rows = useMemo(() => (exam ? GROUPS.flatMap((g) => exam.groups[g]) : []), [exam])
  const invalid = rows.filter((row) => !normalizeCell(row.group, values[cellKey(row)] || '').ok)
  const blank = rows.filter((row) => !(values[cellKey(row)] || '')).length
  const needCode = !!exam && exam.codes.length > 0 && !exam.code
  const unsureCount = rows.filter((row) => unsure.has(cellKey(row))).length

  const setCell = (k: string, raw: string) => {
    setValues((v) => ({ ...v, [k]: raw }))
    setUnsure((u) => { const n = new Set(u); n.delete(k); return n })
  }

  const submit = async () => {
    if (!exam) return
    if (needCode) { setErr('Hãy chọn MÃ ĐỀ in trên đề của em trước khi nộp.'); return }
    if (invalid.length) { setMsg(`Còn ${invalid.length} ô ghi chưa đúng mẫu (đỏ).`); return }
    if (!identityOk(identity)) { setErr('Hãy nhập đủ HỌ TÊN và LỚP ở cuối bảng trước khi nộp.'); return }
    saveIdentity(identity)
    setChecked(false); setChecking(true)          // bắt buộc xác nhận đã đối chiếu từng ô
  }

  const doSubmit = async () => {
    if (!exam) return
    setChecking(false)
    const clean: Record<string, string> = {}
    rows.forEach((row) => { clean[cellKey(row)] = normalizeCell(row.group, values[cellKey(row)] || '').value })
    setStage('submitting'); setMsg(''); setErr('')
    try {
      const res = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/submit/${exam.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          answers: buildSubmitAnswers(exam, clean),
          question_order: exam.questionOrder,
          option_orders: Object.fromEntries(exam.groups.TN.filter((r) => r.optionOrder.length).map((r) => [r.q.id, r.optionOrder])),
          statement_orders: Object.fromEntries(exam.groups.DS.filter((r) => r.statementOrder.length).map((r) => [r.q.id, r.statementOrder])),
          variant_code: exam.code,
          mode: 'paper',
          ...identityPayload(identity),
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (res.status === 401) {
        setValues(clean)
        needLogin({ kind: 'submit' }, 'Phiên đăng nhập đã hết hạn. Hãy đăng nhập lại — đáp án đã sửa vẫn được giữ.')
        return
      }
      if (!res.ok || !body.success) throw new Error(body.detail || 'Chưa nộp được bài.')
      setValues(clean); setResult(body.data); setStage('result')
      // Câu sai / đúng một phần: mở sẵn hướng dẫn giải.
      setOpenSet(new Set((body.data.results || []).filter((r: any) => !(r.points >= r.max_points && r.max_points > 0)).map((r: any) => r.question_id)))
    } catch (e) {
      setErr((e as Error).message); setStage('confirm')
    }
  }

  const byId = useMemo(() => new Map<string, any>((result?.results || []).map((r: any) => [r.question_id, r])), [result])
  const allOpen = rows.length > 0 && rows.every((r) => openSet.has(r.q.id))
  const toggle = (id: string) => setOpenSet((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const btn = 'inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50'

  if (stage === 'capture') {
    return <ScanCapture title={`Quét phiếu đáp án${exam?.code ? ` · Mã đề ${exam.code}` : ''}`} onClose={onClose} onFinish={(f) => read(f)}
      autoQuad={(src) => { const q = findMarkerQuad(src); return q ? expandQuad(q, 0.07, false) : null }} />
  }

  const codePicker = exam && exam.codes.length > 0 && (
    <div className={`rounded-xl border p-3 flex flex-wrap items-center gap-2 ${needCode ? 'border-rose-300 bg-rose-50' : 'border-amber-200 bg-amber-50'}`}>
      <span className="text-sm font-bold text-slate-800">Mã đề:</span>
      <select value={exam.code || ''} onChange={(e) => setCode(e.target.value || null)}
        className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white font-mono font-bold">
        <option value="">— chọn —</option>
        {exam.codes.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <span className="text-xs text-slate-600">Đối chiếu với mã đề in trên ĐỀ em đã làm — sai mã đề sẽ chấm sai.</span>
    </div>
  )

  return (
    <div className="fixed inset-0 z-[300] bg-slate-900/60 flex sm:items-center sm:justify-center">
      <div className="bg-[#fdfcf0] w-full h-full sm:h-auto sm:max-h-[95vh] sm:max-w-3xl sm:rounded-2xl shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-100 bg-white">
          <ScanLine size={18} className="text-teal-600" />
          <h3 className="font-bold text-slate-800 flex-1 truncate">Nộp bằng phiếu giấy{exam ? ` · ${exam.title}` : ''}{exam?.code ? ` · Mã ${exam.code}` : ''}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Đóng"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {err && <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">{err}</p>}
          {stage === 'login' && (
            <div className="rounded-xl border border-amber-200 bg-white p-5 text-center space-y-3">
              {!err && <p className="text-slate-700">Bạn cần <b>đăng nhập</b> để máy đọc phiếu và chấm bài.</p>}
              <button onClick={() => setLoginOpen(true)} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}><LogIn size={16} /> Đăng nhập</button>
              <p className="text-xs text-slate-500">Đang mở trong Zalo? Hãy bấm "Mở bằng Chrome" ở trang đề trước, vì Zalo chặn đăng nhập Google.</p>
            </div>
          )}
          {(stage === 'loading' || stage === 'reading' || stage === 'submitting') && (
            <p className="text-slate-600 inline-flex items-center gap-2"><Loader2 size={18} className="animate-spin text-teal-600" />
              {stage === 'loading' ? 'Đang tải đề…' : stage === 'reading' ? (readNote || 'Đang đọc phiếu…') : 'Đang chấm bài…'}</p>
          )}

          {stage === 'confirm' && exam && (
            <>
              {codePicker}
              {photo && <img src={photo.url} alt="Phiếu đã chụp" className="w-full max-h-72 object-contain border border-slate-200 rounded-lg bg-white" />}
              {GROUPS.map((g) => exam.groups[g].length > 0 && (
                <section key={g}>
                  <h4 className="font-bold text-slate-800 mb-2">{TITLES[g]}</h4>
                  <div className={`grid gap-2 ${g === 'TN' ? 'grid-cols-5 sm:grid-cols-10' : g === 'DS' ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3 sm:grid-cols-6'}`}>
                    {exam.groups[g].map((row) => {
                      const k = cellKey(row)
                      const v = values[k] || ''
                      const bad = !normalizeCell(g, v).ok
                      const warn = unsure.has(k)
                      const box = `rounded-lg border p-1.5 text-center ${bad ? 'border-rose-400 bg-rose-50' : warn ? 'border-2 border-amber-500 bg-amber-200' : 'border-slate-200 bg-white'}`
                      if (g === 'DS') {
                        // Đúng/Sai: 4 nút a–d, bấm để đổi trống → Đ → S → trống (không gõ được chữ khác)
                        const slots = (v ? dsSlots(v).value : '____').split('')
                        const flip = (i: number) => {
                          const next = [...slots]
                          next[i] = slots[i] === 'Đ' ? 'S' : slots[i] === 'S' ? '_' : 'Đ'
                          setCell(k, next.every((c) => c === '_') ? '' : next.join(''))
                        }
                        return (
                          <div key={k} className={box}>
                            <span className="block text-xs font-bold text-slate-500">{row.number}</span>
                            <div className="flex justify-center gap-1 mt-0.5">
                              {slots.map((c, i) => (
                                <button key={i} type="button" onClick={() => flip(i)} aria-label={`Ý ${'abcd'[i]}`}
                                  className={`w-9 h-10 rounded-md border text-base font-black leading-none flex flex-col items-center justify-center ${c === '_' ? 'bg-white border-dashed border-slate-300 text-slate-300' : 'bg-white border-slate-400 text-slate-900'}`}>
                                  <span className="text-[10px] font-semibold text-slate-400">{'abcd'[i]}</span>{c === '_' ? '–' : c}
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      }
                      return (
                        <label key={k} className={box}>
                          <span className="block text-xs font-bold text-slate-500">{row.number}</span>
                          <input value={v} onChange={(e) => setCell(k, typedCell(g, e.target.value))}
                            maxLength={g === 'TN' ? 1 : 8} inputMode={g === 'TL' ? 'decimal' : 'text'}
                            className="w-full text-center font-mono text-lg font-bold uppercase bg-transparent outline-none" />
                        </label>
                      )
                    })}
                  </div>
                </section>
              ))}
              <p className="text-xs text-slate-500">Nền vàng = máy đọc chưa chắc · Đỏ = ghi chưa đúng mẫu (A–D; Phần II bấm từng ý để chọn Đ hoặc S; số như 1,2). Để trống = bỏ câu đó.</p>
              <SubmitIdentityFields value={identity} onChange={setIdentity} />
            </>
          )}

          {/* Cỡ chữ px cố định: CSS chung ép .text-sm/.text-base… nhỏ lại trên điện thoại (!important) */}
          {stage === 'result' && result && result.show_score === false && (
            <p className="rounded-xl bg-teal-50 border border-teal-100 p-4 text-teal-800 text-[17px] font-semibold">✅ Đã nộp bài. Kết quả chi tiết không được công bố theo cài đặt của đề.</p>
          )}
          {stage === 'result' && result && result.show_score !== false && exam && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl bg-blue-50 p-3 text-center">
                  <div className="text-4xl font-black text-blue-700">{scale10(result.score, result.max_score)}</div>
                  <div className="text-[15px] font-semibold text-blue-600">Điểm thang 10{exam.code ? ` · Mã ${exam.code}` : ''}</div>
                </div>
                {GROUPS.map((g) => exam.groups[g].length > 0 && (
                  <div key={g} className="rounded-xl bg-white border border-slate-200 p-3 text-center">
                    <div className="text-[24px] font-bold text-slate-800">{result.section_scores?.[g]?.score ?? 0}/{result.section_scores?.[g]?.max_score ?? 0}</div>
                    <div className="text-[15px] text-slate-600">{TITLES[g]}</div>
                  </div>
                ))}
              </div>

              <StudentAIReviewPanel attemptId={result.attempt_id} />

              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-teal-600" />
                <span className="text-[17px] text-slate-700 flex-1">Hướng dẫn giải từng câu (câu sai đã được mở sẵn)</span>
                <button onClick={() => setOpenSet(allOpen ? new Set() : new Set(rows.map((r) => r.q.id)))}
                  className="px-3 py-2 rounded-lg bg-white border border-teal-200 text-teal-700 text-[17px] font-bold hover:bg-teal-50">
                  {allOpen ? 'Thu gọn tất cả' : 'Mở tất cả'}
                </button>
              </div>

              {GROUPS.map((g) => exam.groups[g].length > 0 && (
                <section key={g}>
                  <h4 className="text-[19px] font-bold text-slate-800 mb-2">{TITLES[g]}</h4>
                  <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                    {exam.groups[g].map((row) => {
                      const q = row.q
                      const r = byId.get(q.id) || {}
                      const right = r.points >= r.max_points && r.max_points > 0
                      const partial = !right && r.points > 0
                      const expanded = openSet.has(q.id)
                      const sol = solutionOnSheet(row, r)
                      const ctx = clusterContextOf(exam, row)
                      return (
                        <div key={q.id}>
                          <button onClick={() => toggle(q.id)} className="w-full flex flex-wrap items-center gap-x-2.5 gap-y-1 px-3 py-3 text-left hover:bg-slate-50">
                            {right ? <CheckCircle2 size={22} className="text-teal-600 shrink-0" /> : partial ? <AlertTriangle size={22} className="text-amber-500 shrink-0" /> : <XCircle size={22} className="text-rose-500 shrink-0" />}
                            <span className="w-[4.5rem] shrink-0 text-[17px] font-bold text-slate-800">Câu {row.number}</span>
                            <span className="order-last basis-full pl-[32px] text-[17px] leading-snug">
                              <span className="whitespace-nowrap">Em ghi: <b className="font-mono text-[19px]">{values[cellKey(row)] || '—'}</b></span>{' '}
                              <span className="whitespace-nowrap">· Đáp án: <b className="font-mono text-[19px] text-teal-700">{correctOnSheet(row, r)}</b></span>
                            </span>
                            <span className="ml-auto shrink-0 text-[15px] font-semibold text-slate-600">{r.points ?? 0}/{r.max_points ?? 0}đ</span>
                            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                          {expanded && (
                            <div className="px-4 pb-4 text-[17px] leading-relaxed text-slate-800 space-y-3">
                              {ctx && (
                                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                                  <p className="text-[15px] font-bold text-amber-700 mb-1">Phần chung (câu hỏi chùm)</p>
                                  <KaTeXRenderer content={ctx} />
                                </div>
                              )}
                              {(q.question_lead || q.cau_hoi) && <KaTeXRenderer content={q.question_lead || q.cau_hoi} />}
                              <div className="rounded-lg bg-teal-50 border border-teal-100 p-3">
                                <p className="text-[15px] font-bold text-teal-700 mb-1">Hướng dẫn giải</p>
                                {sol ? <KaTeXRenderer content={sol} /> : <p className="text-slate-500">Đề chưa có lời giải cho câu này.</p>}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </section>
              ))}
            </>
          )}

          {msg && <p className="text-sm text-slate-700">{msg}</p>}
        </div>

        {stage === 'confirm' && (
          <div className="border-t border-amber-100 bg-white px-4 py-3 flex flex-wrap gap-2">
            <button onClick={() => setStage('capture')} className={`${btn} bg-white border border-slate-200 text-slate-700 hover:bg-slate-50`}><RotateCcw size={15} /> Chụp lại</button>
            <button onClick={submit} className={`${btn} ml-auto bg-blue-600 text-white hover:bg-blue-700`}><Send size={15} /> Nộp bài</button>
          </div>
        )}
        {checking && exam && (
          <div className="fixed inset-0 z-[320] bg-slate-900/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3">
              <h4 className="font-black text-lg text-slate-800 inline-flex items-center gap-2"><AlertTriangle size={20} className="text-amber-500" /> Kiểm tra lại trước khi nộp</h4>
              <ul className="text-sm text-slate-700 space-y-1.5">
                <li>Mã đề: <b>{exam.code || 'đề gốc'}</b> — đúng với mã in trên đề em làm?</li>
                <li>Họ tên: <b>{identity.name}</b> · Lớp <b>{identity.className}</b> · Môn <b>{identity.subject}</b></li>
                {unsureCount > 0 && <li className="rounded-lg bg-amber-50 border border-amber-200 p-2 font-semibold text-amber-800">⚠️ Còn {unsureCount} ô máy đọc CHƯA CHẮC (tô vàng). Hãy so lại từng ô đó với phiếu giấy.</li>}
                {blank > 0 && <li className="font-semibold text-rose-600">Còn {blank} câu để trống — sẽ được 0 điểm.</li>}
                <li>Máy đọc chữ viết tay có thể nhầm (A↔D, 1↔7, Đ↔S…). Bài được chấm ngay theo đúng bảng trên màn hình.</li>
              </ul>
              <label className="flex items-start gap-2 rounded-lg bg-teal-50 border border-teal-200 p-2.5 text-sm font-semibold text-teal-900">
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 w-4 h-4 accent-teal-600" />
                Em đã đối chiếu TỪNG Ô trên màn hình với phiếu giấy và chắc chắn đáp án đúng như em đã ghi.
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setChecking(false)} className={`${btn} bg-white border border-slate-200 text-slate-700 hover:bg-slate-50`}>Kiểm tra lại</button>
                <button onClick={doSubmit} disabled={!checked} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}><Send size={15} /> Nộp bài</button>
              </div>
            </div>
          </div>
        )}
        {stage === 'result' && (
          <div className="border-t border-amber-100 bg-white px-4 py-3 flex justify-end">
            <button onClick={onClose} className={`${btn} bg-teal-600 text-white hover:bg-teal-700`}>Xong</button>
          </div>
        )}
      </div>
      {/* LoginModal dùng z-50 — bọc z cao hơn để nổi trên cửa sổ phiếu (z-300) */}
      <div className="relative z-[500]">
        <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    </div>
  )
}

export default PaperSheetSubmit
